import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { CreditCard } from 'lucide-react';
export function PaymentPage() {
  const navigate = useNavigate();
  const {
    reportId
  } = useParams();
  const location = useLocation();
  const [isProcessing, setIsProcessing] = useState(false);
  // Detect product type from URL path
  const getProductType = () => {
    if (location.pathname.includes('/work/')) return 'work';
    if (location.pathname.includes('/child/')) return 'child';
    if (location.pathname.includes('/relationship/')) return 'relationship';
    return 'self';
  };
  const product = getProductType();
  const getProductConfig = () => {
    switch (product) {
      case 'work':
        return {
          name: 'Work System Diagnostic',
          icon: '💼',
          description: 'Complete work system architecture and career trajectory analysis'
        };
      case 'child':
        return {
          name: 'Child System Map',
          icon: '👶',
          description: 'Complete child temperament and developmental wiring analysis'
        };
      case 'relationship':
        return {
          name: 'Relationship Dynamics Diagnostic',
          icon: '💞',
          description: 'Complete two-system interaction and dynamics analysis'
        };
      default:
        // self
        return {
          name: 'Self System Diagnostic',
          icon: '🧬',
          description: 'Complete system architecture and behavioral pattern analysis'
        };
    }
  };
  const config = getProductConfig();
  // Mock user data (in production, this would come from state/API)
  const reportData = {
    productName: config.name,
    productIcon: config.icon,
    price: '$499',
    regularPrice: '$1,499',
    userName: 'Anish',
    dateOfBirth: '19 June 1989'
  };
  const handlePayment = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsProcessing(true);
    // Simulate payment processing
    setTimeout(() => {
      // In production, this would call payment gateway
      // On success, redirect to full report with correct path
      const fullReportPath = product === 'self' ? `/report/${reportId}?state=full` : `/report/${product}/${reportId}?state=full`;
      navigate(fullReportPath);
    }, 2000);
  };
  return <>
      <Helmet data-id="element-2210">
        <title data-id="element-2211">Payment — Unlock {config.name} | Lazy Monk Labs</title>
        <meta name="robots" content="noindex, nofollow" data-id="element-2212" />
      </Helmet>

      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5]" data-id="element-2213">
        {/* Navigation */}
        <header data-id="element-2214">
          <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#2B2B2B]" data-id="element-2215">
            <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-2216">
              <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-id="element-2217">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jXd6KyuddrgAfqwwkkpUrR/Lazy_Monk_Labs_Logo.png" alt="Lazy Monk Labs Logo" className="h-8 w-auto" width="32" height="32" data-id="element-2218" />
                <span className="text-lg font-medium tracking-tight" data-id="element-2219">
                  Lazy Monk Labs
                </span>
              </Link>
              <div className="flex items-center gap-8" data-id="element-2220">
                <Link to="/decode" className="text-sm text-[#F5F5F5]/80 hover:text-[#F5F5F5] transition-colors" data-id="element-2221">
                  Decode
                </Link>
                <a href="https://wa.me/919633096997" target="_blank" rel="noopener noreferrer" className="text-sm text-[#F5F5F5]/80 hover:text-[#F5F5F5] transition-colors" data-id="element-2222">
                  Contact
                </a>
              </div>
            </div>
          </nav>
        </header>

        <main className="pt-32 pb-24 px-6" data-id="element-2223">
          <div className="max-w-4xl mx-auto" data-id="element-2224">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-2225">
              {/* Left: Order Summary */}
              <div data-id="element-2226">
                <h1 className="text-3xl font-medium mb-8" data-id="element-2227">Order Summary</h1>

                <div className="border border-[#2B2B2B] p-6 bg-[#111214] mb-6" data-id="element-2228">
                  <div className="flex items-center gap-3 mb-4" data-id="element-2229">
                    <span className="text-3xl" data-id="element-2230">{reportData.productIcon}</span>
                    <h2 className="text-xl font-medium" data-id="element-2231">
                      {reportData.productName}
                    </h2>
                  </div>
                  <div className="space-y-2 text-sm text-[#F5F5F5]/60 mb-6" data-id="element-2232">
                    <p data-id="element-2233">
                      <strong className="text-[#F5F5F5]/80" data-id="element-2234">Name:</strong>{' '}
                      {reportData.userName}
                    </p>
                    <p data-id="element-2235">
                      <strong className="text-[#F5F5F5]/80" data-id="element-2236">
                        Date of Birth:
                      </strong>{' '}
                      {reportData.dateOfBirth}
                    </p>
                  </div>

                  <div className="pt-6 border-t border-[#2B2B2B]" data-id="element-2237">
                    <div className="flex justify-between items-center mb-2" data-id="element-2238">
                      <span className="text-[#F5F5F5]/70" data-id="element-2239">Full Diagnostic</span>
                      <div className="text-right" data-id="element-2240">
                        <div className="text-[#F5F5F5] font-medium" data-id="element-2241">
                          {reportData.price}
                        </div>
                        <div className="text-xs text-[#F5F5F5]/40 line-through" data-id="element-2242">
                          Regular: {reportData.regularPrice}
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-between items-center text-xl font-medium pt-4 border-t border-[#2B2B2B] mt-4" data-id="element-2243">
                      <span data-id="element-2244">Total</span>
                      <span data-id="element-2245">{reportData.price}</span>
                    </div>
                    <p className="text-xs text-[#64748B] mt-2" data-id="element-2246">
                      Launch pricing — Save $
                      {parseInt(reportData.regularPrice.replace(/[^0-9]/g, '')) - parseInt(reportData.price.replace(/[^0-9]/g, ''))}
                    </p>
                  </div>
                </div>

                <div className="border border-[#2B2B2B] p-6 bg-[#111214]" data-id="element-2247">
                  <h3 className="text-sm font-medium mb-4 text-[#F5F5F5]/80" data-id="element-2248">
                    What's Included
                  </h3>
                  <ul className="space-y-2 text-sm text-[#F5F5F5]/60" data-id="element-2249">
                    <li data-id="element-2250">• {config.description}</li>
                    <li data-id="element-2251">• All behavioral patterns and loops</li>
                    <li data-id="element-2252">• Stress and pressure response modes</li>
                    <li data-id="element-2253">• Structural strengths and constraints</li>
                    <li data-id="element-2254">• Lifetime access to your report</li>
                  </ul>
                </div>
              </div>

              {/* Right: Payment Form */}
              <div data-id="element-2255">
                <h2 className="text-2xl font-medium mb-8" data-id="element-2256">Payment Details</h2>

                <form onSubmit={handlePayment} className="space-y-6" data-id="element-2257">
                  {/* Card Number */}
                  <div data-id="element-2258">
                    <label htmlFor="cardNumber" className="block text-sm font-medium mb-2" data-id="element-2259">
                      Card Number
                    </label>
                    <div className="relative" data-id="element-2260">
                      <input type="text" id="cardNumber" placeholder="1234 5678 9012 3456" required className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] placeholder-[#F5F5F5]/40 focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-2261" />
                      <CreditCard className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#F5F5F5]/40" data-id="element-2262" />
                    </div>
                  </div>

                  {/* Expiry and CVC */}
                  <div className="grid grid-cols-2 gap-4" data-id="element-2263">
                    <div data-id="element-2264">
                      <label htmlFor="expiry" className="block text-sm font-medium mb-2" data-id="element-2265">
                        Expiry Date
                      </label>
                      <input type="text" id="expiry" placeholder="MM / YY" required className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] placeholder-[#F5F5F5]/40 focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-2266" />
                    </div>
                    <div data-id="element-2267">
                      <label htmlFor="cvc" className="block text-sm font-medium mb-2" data-id="element-2268">
                        CVC
                      </label>
                      <input type="text" id="cvc" placeholder="123" required className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] placeholder-[#F5F5F5]/40 focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-2269" />
                    </div>
                  </div>

                  {/* Cardholder Name */}
                  <div data-id="element-2270">
                    <label htmlFor="cardholderName" className="block text-sm font-medium mb-2" data-id="element-2271">
                      Cardholder Name
                    </label>
                    <input type="text" id="cardholderName" placeholder="Name on card" required className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] placeholder-[#F5F5F5]/40 focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-2272" />
                  </div>

                  {/* Submit Button */}
                  <button type="submit" disabled={isProcessing} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2273">
                    {isProcessing ? 'Processing...' : `Pay ${reportData.price} & Unlock Report`}
                  </button>

                  <p className="text-xs text-[#F5F5F5]/40 text-center" data-id="element-2274">
                    Secure payment processing. Your data is encrypted.
                  </p>
                </form>
              </div>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="py-8 px-6 border-t border-[#2B2B2B]" data-id="element-2275">
          <div className="max-w-7xl mx-auto text-center" data-id="element-2276">
            <p className="text-xs text-[#F5F5F5]/40" data-id="element-2277">
              © {new Date().getFullYear()} Lazy Monk Labs. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </>;
}